import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown, Loader2, Music as MusicIcon, Tag, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "@/lib/api";

const YT_THUMB = (id) => `https://img.youtube.com/vi/${id}/mqdefault.jpg`;

/** Default genre presets. "Outro" unlocks a free-form input for a custom label. */
const GENRE_PRESETS = ["Gospel", "Rock", "Sertanejo", "Pagode", "Funk", "Pop", "MPB", "Infantil", "Outro"];

/** Extract YouTube ID from various URL formats or raw ID. */
const parseYouTubeId = (input) => {
  if (!input) return "";
  const s = input.trim();
  let m = s.match(/youtu\.be\/([A-Za-z0-9_-]{6,20})/);
  if (m) return m[1];
  m = s.match(/[?&]v=([A-Za-z0-9_-]{6,20})/);
  if (m) return m[1];
  m = s.match(/youtube\.com\/(?:embed|live|shorts)\/([A-Za-z0-9_-]{6,20})/);
  if (m) return m[1];
  if (/^[A-Za-z0-9_-]{10,12}$/.test(s)) return s;
  return "";
};

const AdminMusic = () => {
  const [tracks, setTracks] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", artist: "", url: "", genrePreset: "Sertanejo", genreCustom: "" });
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editGenre, setEditGenre] = useState({ preset: "Sertanejo", custom: "" });

  const load = async () => {
    try {
      const { data } = await adminApi.get("/admin/music");
      setTracks(data);
    } catch {}
  };

  useEffect(() => {
    load();
  }, []);

  const resolveGenre = (preset, custom) => (preset === "Outro" ? (custom || "").trim() : preset);

  const submit = async (e) => {
    e.preventDefault();
    const yid = parseYouTubeId(form.url);
    const genre = resolveGenre(form.genrePreset, form.genreCustom);
    if (!form.title || !form.artist || !yid) {
      toast.error("Preencha título, artista e uma URL ou ID válido do YouTube.");
      return;
    }
    if (form.genrePreset === "Outro" && !genre) {
      toast.error("Digite o nome da categoria personalizada.");
      return;
    }
    setBusy(true);
    try {
      await adminApi.post("/admin/music", {
        title: form.title.trim(),
        artist: form.artist.trim(),
        youtube_id: yid,
        genre,
      });
      toast.success(`Música adicionada em "${genre}".`);
      setForm({ title: "", artist: "", url: "", genrePreset: form.genrePreset, genreCustom: form.genreCustom });
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (id) => {
    try {
      await adminApi.post(`/admin/music/${id}/toggle`);
      load();
    } catch {}
  };

  const move = async (id, direction) => {
    try {
      await adminApi.post(`/admin/music/${id}/move?direction=${direction}`);
      load();
    } catch {}
  };

  const remove = async (id) => {
    if (!window.confirm("Remover esta música da playlist?")) return;
    try {
      await adminApi.delete(`/admin/music/${id}`);
      toast.success("Removida.");
      load();
    } catch {}
  };

  const startEdit = (t) => {
    const isPreset = GENRE_PRESETS.slice(0, -1).includes(t.genre); // exclude "Outro"
    setEditingId(t.id);
    setEditGenre({
      preset: isPreset ? t.genre : (t.genre ? "Outro" : "Sertanejo"),
      custom: isPreset ? "" : (t.genre || ""),
    });
  };

  const saveEdit = async (id) => {
    const genre = resolveGenre(editGenre.preset, editGenre.custom);
    if (editGenre.preset === "Outro" && !genre) {
      toast.error("Digite o nome da categoria personalizada.");
      return;
    }
    try {
      await adminApi.put(`/admin/music/${id}`, { genre });
      setEditingId(null);
      toast.success("Categoria atualizada.");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const categoryCounts = useMemo(() => {
    const map = {};
    tracks.forEach((t) => {
      const key = t.genre || "Sem categoria";
      map[key] = (map[key] || 0) + 1;
    });
    return map;
  }, [tracks]);

  return (
    <div>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-[var(--fi-red)] font-bold mb-1">
            Fala Music
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-black uppercase text-[var(--fi-blue)] leading-tight">
            Playlist
          </h1>
          <p className="text-gray-500 mt-1">
            Vídeos musicais do YouTube em loop no app. Toca na ordem, autoplay, sem custos de licenciamento.
          </p>
        </div>
        <button
          data-testid="admin-music-new"
          onClick={() => setShowForm((s) => !s)}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[var(--fi-red)] hover:bg-[var(--fi-red-hover)] text-white font-bold uppercase tracking-widest text-xs"
        >
          <Plus className="w-4 h-4" />
          {showForm ? "Fechar" : "Nova música"}
        </button>
      </div>

      {/* Category summary chips */}
      {Object.keys(categoryCounts).length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="admin-music-category-chips">
          {Object.entries(categoryCounts).map(([cat, count]) => (
            <span
              key={cat}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest bg-white text-[var(--fi-blue)] border border-gray-200"
            >
              <Tag className="w-3 h-3" />
              {cat}
              <span className="text-[10px] text-gray-500 font-normal">· {count}</span>
            </span>
          ))}
        </div>
      )}

      {showForm && (
        <form
          onSubmit={submit}
          className="mt-5 rounded-xl bg-white p-5 shadow-[0_8px_30px_rgba(13,27,75,0.06)] space-y-3"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Labeled label="Título da música *">
              <input
                data-testid="admin-music-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="input"
                placeholder="Ex: Evidências"
              />
            </Labeled>
            <Labeled label="Artista *">
              <input
                data-testid="admin-music-artist"
                value={form.artist}
                onChange={(e) => setForm({ ...form, artist: e.target.value })}
                className="input"
                placeholder="Ex: Chitãozinho e Xororó"
              />
            </Labeled>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Labeled label="Tipo / Gênero *">
              <select
                data-testid="admin-music-genre"
                value={form.genrePreset}
                onChange={(e) => setForm({ ...form, genrePreset: e.target.value })}
                className="input bg-white"
              >
                {GENRE_PRESETS.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </Labeled>
            {form.genrePreset === "Outro" && (
              <Labeled label="Nome da categoria personalizada *">
                <input
                  data-testid="admin-music-genre-custom"
                  value={form.genreCustom}
                  onChange={(e) => setForm({ ...form, genreCustom: e.target.value })}
                  className="input"
                  placeholder="Ex: Forró, Reggae, Bossa Nova…"
                />
              </Labeled>
            )}
          </div>

          <Labeled label="URL ou ID do YouTube *">
            <input
              data-testid="admin-music-url"
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              className="input"
              placeholder="https://youtu.be/xxxxxxxx  ou  xxxxxxxxxxx"
            />
            <div className="text-xs text-gray-500 mt-1">
              Aceita formato completo (youtube.com/watch?v=…, youtu.be/…) ou apenas o ID.
            </div>
          </Labeled>
          <button
            data-testid="admin-music-submit"
            disabled={busy}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[var(--fi-blue)] hover:bg-[var(--fi-blue-2)] text-white font-bold uppercase tracking-widest text-xs disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Adicionar à playlist"}
          </button>
          <style>{`.input{width:100%;padding:10px 12px;border:1px solid #e5e7eb;border-radius:10px;font-size:14px;outline:none;transition:border-color .15s}.input:focus{border-color:#0D1B4B}`}</style>
        </form>
      )}

      <div className="mt-6 space-y-2">
        {tracks.length === 0 && (
          <div className="text-center py-16 text-gray-400 font-medium">
            <MusicIcon className="w-10 h-10 mx-auto text-gray-300 mb-3" />
            Nenhuma música na playlist ainda.
          </div>
        )}
        {tracks.map((t, i) => (
          <div
            key={t.id}
            className="rounded-xl bg-white p-3 shadow-[0_6px_20px_rgba(13,27,75,0.05)] flex items-center gap-3"
          >
            <div className="flex flex-col">
              <button
                onClick={() => move(t.id, "up")}
                disabled={i === 0}
                className="w-6 h-6 rounded text-gray-400 hover:text-[var(--fi-blue)] disabled:opacity-30 flex items-center justify-center"
                aria-label="Subir na ordem"
                data-testid={`admin-music-up-${t.id}`}
              >
                <ArrowUp className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => move(t.id, "down")}
                disabled={i === tracks.length - 1}
                className="w-6 h-6 rounded text-gray-400 hover:text-[var(--fi-blue)] disabled:opacity-30 flex items-center justify-center"
                aria-label="Descer na ordem"
                data-testid={`admin-music-down-${t.id}`}
              >
                <ArrowDown className="w-3.5 h-3.5" />
              </button>
            </div>
            <img src={YT_THUMB(t.youtube_id)} alt="" className="w-20 h-12 object-cover rounded" />
            <div className="flex-1 min-w-0">
              <div className="font-bold text-[var(--fi-blue)] truncate">{t.title}</div>
              <div className="text-xs text-gray-500 truncate">
                {t.artist} · YouTube: {t.youtube_id}
              </div>
              {editingId === t.id ? (
                <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                  <select
                    data-testid={`admin-music-edit-genre-${t.id}`}
                    value={editGenre.preset}
                    onChange={(e) => setEditGenre({ ...editGenre, preset: e.target.value })}
                    className="text-xs px-2 py-1 border border-gray-200 rounded"
                  >
                    {GENRE_PRESETS.map((g) => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                  {editGenre.preset === "Outro" && (
                    <input
                      data-testid={`admin-music-edit-genre-custom-${t.id}`}
                      value={editGenre.custom}
                      onChange={(e) => setEditGenre({ ...editGenre, custom: e.target.value })}
                      placeholder="Categoria…"
                      className="text-xs px-2 py-1 border border-gray-200 rounded"
                    />
                  )}
                  <button
                    onClick={() => saveEdit(t.id)}
                    data-testid={`admin-music-save-genre-${t.id}`}
                    className="w-7 h-7 rounded bg-emerald-500 text-white flex items-center justify-center hover:bg-emerald-600"
                    aria-label="Salvar"
                  >
                    <Check className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => setEditingId(null)}
                    className="w-7 h-7 rounded bg-gray-200 text-gray-600 flex items-center justify-center hover:bg-gray-300"
                    aria-label="Cancelar"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => startEdit(t)}
                  data-testid={`admin-music-edit-${t.id}`}
                  className="inline-flex items-center gap-1 mt-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-[var(--fi-blue)]/5 text-[var(--fi-blue)] hover:bg-[var(--fi-blue)]/10 transition-colors"
                >
                  <Tag className="w-3 h-3" />
                  {t.genre || "Sem categoria"}
                  <Pencil className="w-2.5 h-2.5 opacity-60" />
                </button>
              )}
            </div>
            <button
              onClick={() => toggle(t.id)}
              data-testid={`admin-music-toggle-${t.id}`}
              className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-widest font-bold transition-colors ${
                t.active
                  ? "bg-emerald-500 text-white hover:bg-emerald-600"
                  : "bg-gray-200 text-gray-600 hover:bg-gray-300"
              }`}
            >
              {t.active ? "Ativa" : "Pausada"}
            </button>
            <button
              onClick={() => remove(t.id)}
              data-testid={`admin-music-delete-${t.id}`}
              className="w-9 h-9 rounded-lg text-gray-400 hover:text-[var(--fi-red)] hover:bg-red-50 flex items-center justify-center transition-colors"
              aria-label="Remover"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const Labeled = ({ label, children }) => (
  <div>
    <label className="block text-xs uppercase tracking-widest font-bold text-[var(--fi-blue)] mb-1.5">
      {label}
    </label>
    {children}
  </div>
);

export default AdminMusic;
